import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { twMerge } from 'tailwind-merge';

const TreeNode = ({ node, isRoot = false, layoutMode = 'vertical', onNodeClick, onToggle, theme }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    if (onToggle) onToggle();
  };
  
  const handleNodeClick = (e) => {
    e.stopPropagation();
    if (onNodeClick) {
      onNodeClick(node);
    }
  };

  const getCleanTitle = (html) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  // 🎨 Dynamic Styling Logic
  const getNodeConfig = (type, customColor, customIcon) => {
    const base = "backdrop-blur-md transition-all duration-300 border relative";
    
    // Icon Logic: Use custom if available, otherwise only Root gets a default.
    let icon = customIcon ? FiIcons[customIcon] : (type === 'root' ? FiIcons.FiHome : null);
    
    // If a custom color is set on the NODE itself (overrides theme)
    if (customColor) {
      return {
        container: `${base} shadow-lg`,
        style: {
          backgroundColor: theme?.nodeBg || '#ffffff',
          borderLeft: `4px solid ${customColor}`,
          borderColor: customColor,
        },
        icon: icon, // Might be null
        iconColorStyle: { color: customColor },
        glowStyle: {
           background: `linear-gradient(to bottom right, ${customColor}20, transparent)`
        }
      };
    }

    // Default styles using Theme
    const primaryColor = theme?.primary || '#6366f1';
    
    switch (type) {
      case 'root':
        return {
          container: `${base} text-white shadow-xl`,
          style: {
            backgroundColor: '#1e293b', 
            borderColor: '#334155'
          },
          icon: icon,
          iconColorStyle: { color: '#60a5fa' }, // Blue-400
          glowStyle: { background: 'rgba(59, 130, 246, 0.2)' }
        };
      case 'category':
        return {
          container: `${base} shadow-lg`,
          style: {
            backgroundColor: theme?.nodeBg || '#ffffff',
            borderColor: primaryColor, 
            borderLeftWidth: '4px',
            borderLeftColor: primaryColor,
            color: theme?.textColor || '#1e293b'
          },
          icon: icon,
          iconColorStyle: { color: primaryColor },
          glowStyle: { background: `${primaryColor}10` }
        };
      default: // 'page'
        return {
          container: `${base} hover:shadow-lg`,
          style: {
            backgroundColor: theme?.nodeBg || '#ffffff',
            borderColor: '#e2e8f0', 
            color: theme?.textColor || '#1e293b'
          },
          icon: icon,
          iconColorStyle: { color: '#94a3b8' },
          glowStyle: { background: 'transparent' }
        };
    }
  };

  const config = getNodeConfig(node.type, node.color, node.icon);

  // Position toggle button
  // Vertical: Centered horizontally, pushed down 12px (half height) to center on the line
  const expandBtnClass = layoutMode === 'vertical' 
    ? "absolute -bottom-3 left-1/2 -translate-x-1/2 z-20" 
    : "absolute -right-3 top-1/2 -translate-y-1/2 z-20";

  return (
    <li className={twMerge("tree-li", isRoot && "tree-root")}>
      <motion.div 
        layout 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="relative z-10 group node-wrapper"
      >
        <div 
          id={`node-${node.id}`}
          onClick={handleNodeClick}
          title={getCleanTitle(node.label)}
          className={twMerge(
            "relative flex flex-col items-center justify-center py-3 px-5 min-w-[160px] max-w-[240px] rounded-xl cursor-default overflow-hidden",
            "hover:scale-[1.02] transition-transform duration-300 cursor-pointer", 
            config.container
          )}
          style={config.style}
        >
          {/* Subtle Glow Effect */}
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style={config.glowStyle}
          />

          <div className="flex items-center gap-2.5 mb-1 relative z-10 w-full justify-center">
            {/* ONLY Render Icon if one exists */}
            {config.icon && (
              <div 
                className="p-1.5 rounded-lg bg-black/5 shadow-inner shrink-0"
                style={config.iconColorStyle}
              >
                <SafeIcon icon={config.icon} className="text-lg" />
              </div>
            )}
            
            {/* RICH TEXT LABEL RENDERER */}
            <div 
              className="font-semibold text-sm tracking-wide truncate max-w-[160px] [&>span]:text-xs [&>span]:opacity-70 [&>span]:font-normal selection:bg-indigo-100 [&>p]:inline [&>p]:m-0"
              dangerouslySetInnerHTML={{ __html: node.label }}
            />
          </div>

          {node.description && (
            <p className="text-[11px] opacity-70 leading-tight mt-1 max-w-[160px] text-center font-medium relative z-10">
              {node.description}
            </p>
          )}
        </div>

        {hasChildren && (
          <button 
            onClick={toggleExpand}
            className={twMerge(
              "w-6 h-6 rounded-full flex items-center justify-center text-[10px] transition-all shadow-md",
              "bg-white border hover:scale-110",
              expandBtnClass
            )}
            style={{ 
              borderColor: isExpanded ? '#e2e8f0' : (theme?.primary || '#6366f1'),
              color: isExpanded ? '#94a3b8' : (theme?.primary || '#6366f1'),
              backgroundColor: isExpanded ? '#ffffff' : '#f8fafc'
            }}
            title={isExpanded ? "Collapse" : "Expand"}
          >
            <SafeIcon icon={isExpanded ? FiIcons.FiMinus : FiIcons.FiPlus} className={twMerge("transition-transform duration-300", isExpanded && "rotate-180")} />
          </button>
        )}
      </motion.div>

      <AnimatePresence mode='wait'>
        {hasChildren && isExpanded && (
          <motion.ul 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            className="tree-ul"
          >
            {node.children.map((child) => (
              <TreeNode 
                key={child.id || Math.random()} 
                node={child} 
                layoutMode={layoutMode} 
                onNodeClick={onNodeClick} 
                onToggle={onToggle}
                theme={theme}
              />
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
};

export default TreeNode;